/**
 * VoiceAssignmentPanel — configure per-agent Kokoro voice blend.
 * Renders in RegistryTab when an orchestrator is selected.
 * "Save & Preview" saves the blend, triggers cache refresh, then plays preview audio.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  agentVoiceApi, KOKORO_VOICES, VoiceBlendEntry,
} from '@/services/api/agentApi';
import { getCurrentAccessToken } from '@/services/api/auth-session';
import { kokoroTTS } from '@/services/voice/KokoroWebSocketTTS';

interface Props {
  orchestratorId: string;
  currentBlend?: VoiceBlendEntry[];
}

const PREVIEW_TEXT = "Hello, I'm your TerraMatrix agent. How can I help you today?";

const getOptionalEnvString = (value: unknown): string | undefined => (
  typeof value === 'string' && value.trim() !== '' ? value : undefined
);

const getInitialBlend = (currentBlend?: VoiceBlendEntry[]): VoiceBlendEntry[] => (
  currentBlend?.length ? currentBlend : [{ voice_name: 'af_heart', weight: 1.0 }]
);

const VOICE_SERVICE_HTTP = (
  getOptionalEnvString(import.meta.env.VITE_VOICE_SERVICE_URL) ?? 'ws://localhost:8007'
).replace(/^ws:\/\//, 'http://').replace(/^wss:\/\//, 'https://');

export const VoiceAssignmentPanel: React.FC<Props> = ({ orchestratorId, currentBlend }) => {
  const [blend, setBlend] = useState<VoiceBlendEntry[]>(getInitialBlend(currentBlend));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  // Sync when a different agent is selected
  useEffect(() => {
    setBlend(getInitialBlend(currentBlend));
    setMsg(null);
  }, [currentBlend, orchestratorId]);

  const totalW = blend.reduce((s, e) => s + e.weight, 0);
  const pct = (w: number) => (totalW > 0 ? Math.round((w / totalW) * 100) : 0);

  const updateVoice = (i: number, v: string) =>
    setBlend(b => b.map((e, idx) => idx === i ? { ...e, voice_name: v } : e));
  const updateWeight = (i: number, w: number) =>
    setBlend(b => b.map((e, idx) => idx === i ? { ...e, weight: w } : e));
  const addEntry = () => setBlend(b => [...b, { voice_name: 'af_heart', weight: 0.5 }]);
  const removeEntry = (i: number) => setBlend(b => b.filter((_, idx) => idx !== i));

  const handleSaveAndPreview = async () => {
    if (!blend.length) { setMsg({ text: 'Add at least one voice', ok: false }); return; }
    setBusy(true);
    setMsg(null);
    try {
      // 1. Save
      const res = await agentVoiceApi.update(orchestratorId, blend);
      if (!res.success) throw new Error('Save failed');

      // 2. Refresh voice-service cache — await so we know it's ready before preview
      const secret = getOptionalEnvString(import.meta.env.VITE_VOICE_SERVICE_SECRET);
      if (secret) {
        try {
          await fetch(`${VOICE_SERVICE_HTTP}/agent-voice/refresh`, {
            method: 'POST',
            headers: { 'x-service-key': secret },
          });
        } catch {
          // Non-fatal — voice-service may not be running locally
        }
      }

      setMsg({ text: 'Saved! Playing preview...', ok: true });

      // 3. Force reconnect so the new agent_id + blended voice are used
      kokoroTTS.disconnect();
      kokoroTTS.setToken(getCurrentAccessToken());
      kokoroTTS.setAgentId(orchestratorId);

      await kokoroTTS.speak(PREVIEW_TEXT, {
        agentId: orchestratorId,
        onEnd: () => setMsg({ text: 'Saved & previewed.', ok: true }),
        onError: () => setMsg({ text: 'Saved but preview failed — check voice-service.', ok: false }),
      });
    } catch (e) {
      setMsg({ text: e instanceof Error ? e.message : 'Error', ok: false });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border p-4 bg-muted/20">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">Voice Blend</span>
        <Button variant="outline" size="sm" onClick={addEntry} disabled={blend.length >= 4}>
          + Add
        </Button>
      </div>

      {blend.map((e, i) => (
        <div key={i} className="flex items-center gap-2">
          <Select value={e.voice_name} onValueChange={v => updateVoice(i, v)}>
            <SelectTrigger className="w-44 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {KOKORO_VOICES.map(v => (
                <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Slider
            min={0.1} max={1} step={0.05}
            value={[e.weight]}
            onValueChange={([w]) => updateWeight(i, w)}
            className="flex-1"
          />
          <span className="text-xs text-muted-foreground w-8 text-right">{pct(e.weight)}%</span>
          <Button
            variant="ghost" size="sm"
            onClick={() => removeEntry(i)}
            disabled={blend.length === 1}
            className="text-destructive text-xs px-1"
          >✕</Button>
        </div>
      ))}

      {msg && (
        <p className={`text-xs ${msg.ok ? 'text-emerald-600' : 'text-destructive'}`}>
          {msg.text}
        </p>
      )}

      <Button className="w-full" size="sm" onClick={handleSaveAndPreview} disabled={busy}>
        {busy ? 'Working...' : 'Save & Preview'}
      </Button>
    </div>
  );
};
